import { SessionInfo, FindhelpProgram } from '@src/api.types';
import { API } from '@src/api';
import { env } from '.abyss/environments.json';
const tokenKey = 'UHC-CC-TOKEN';
const sessionKey = 'UHC-CC-SESSION';
const modeKey = 'UHC-CC-MODE';
const loginRequestKey = 'UHC-CC-LOGIN-REQUEST';
const memberDetailsKey = 'UHC-CC-MEMBER-DETAILS';
const agentDetailsKey = 'UHC-CC-AGENT-DETAILS';
const programsDataKey = 'programsData';
const declineKey = 'UHC-CC-DECLINED-SCREENER';
const bulkReferralKey = 'UHC-CC-BULK-REFERRAL';
const featureFlagsKey = 'UHC-CC-FLAGS';
const identified_NeedsKey = 'Identified-Needs';
const checkDisableScreening = 'UHC-CC-Screening';
const vendorKey = 'UHC-CC-VENDOR';

export const getTokenFromStorage = (): string => {
  return localStorage.getItem(tokenKey) ?? '';
};

export const saveTokenToStorage = (token: string): void => {
  localStorage.setItem(tokenKey, token);
};

export const saveProgramsData = (pData: string): void => {
  localStorage.setItem(programsDataKey, pData);
};

export const getProgramData = (): string => {
  return localStorage.getItem(programsDataKey) ?? '';
};

export const updateDeclinedScreener = (value: string): void => {
  const declinedScreener: string[] = getDeclinedScreener();
  if (declinedScreener.includes(value)) {
    return;
  }

  declinedScreener.push(value);
  sessionStorage.setItem(declineKey, JSON.stringify(declinedScreener));
};

export const getFeatureFlagsFromStorage = (): any => {
  const featureFlags = sessionStorage.getItem(featureFlagsKey) ?? '';
  try {
    return JSON.parse(featureFlags) ?? [];
  } catch (error: any) {
    console.error(
      `Error parsing feature flag data, error was ${error}. Returning an empty array.`
    );
    return [];
  }
};

export const saveFeatureFlagsToStorage = (featureFlags: any): void => {
  sessionStorage.setItem(featureFlagsKey, JSON.stringify(featureFlags));
};

export const saveFromSearch = (show: any): void => {
  sessionStorage.setItem('fromSearch', JSON.stringify(show));
};
export const clearsaveFromSearch = (): void => {
  sessionStorage.removeItem('fromSearch');
};

export const clearFeatureFlagsFromStorage = (): void => {
  sessionStorage.removeItem(featureFlagsKey);
};
// Retrieve an array of the member ID's which have declined to take the needs screener.
// Used to prevent the same member from being prompted to take the screener multiple times.
export const getDeclinedScreener = (): string[] => {
  const declined = sessionStorage.getItem(declineKey) ?? '';
  try {
    return JSON.parse(declined) ?? [];
  } catch (error: any) {
    console.error(
      `Error parsing declined screener data, error was ${error}. Returning an empty array.`
    );
    return [];
  }
};

export const clearDeclinedScreener = (): void => {
  sessionStorage.removeItem(declineKey);
};

export const updateBulkReferral = (program: FindhelpProgram): void => {
  const bulkReferral: FindhelpProgram[] = getBulkReferral();
  if (
    bulkReferral.some((p) => {
      return p.id === program.id;
    })
  ) {
    console.warn(
      'Unexpected behavior. Program already exists in bulk referral'
    );
    return;
  }

  bulkReferral.push(program);
  sessionStorage.setItem(bulkReferralKey, JSON.stringify(bulkReferral));
};

export const removeFromBulkReferral = (programId: string): void => {
  const bulkReferral: FindhelpProgram[] = getBulkReferral();
  const removed = bulkReferral.filter((element) => {
    return element.id !== programId;
  });
  sessionStorage.setItem(bulkReferralKey, JSON.stringify(removed));
};

export const getBulkReferral = (): FindhelpProgram[] => {
  const bulkReferral = sessionStorage.getItem(bulkReferralKey) ?? '[]';
  try {
    return JSON.parse(bulkReferral) ?? [];
  } catch (error: any) {
    console.error(
      `Error parsing bulk referral data, error was ${error}. Returning an empty array.`
    );
    return [];
  }
};

export const saveCheckBenefitScreening = (resp) => {
  sessionStorage.setItem(checkDisableScreening, resp);
};

export const clearCheckDisableScreening = () => {
  sessionStorage.removeItem(checkDisableScreening);
};

export const setBulkReferral = (bulkReferral: FindhelpProgram[]): void => {
  sessionStorage.setItem(bulkReferralKey, JSON.stringify(bulkReferral));
};
export const clearBulkReferral = (): void => {
  sessionStorage.removeItem(bulkReferralKey);
};

export const clearTokenFromStorage = (): void => {
  localStorage.removeItem(tokenKey);
};

// Session ID is a 128 bit UUID, represented as a string.
export const getSessionFromStorage = (): string => {
  return sessionStorage.getItem(sessionKey) ?? '';
};

export const saveSessionToStorage = (sessionId: string): void => {
  return sessionStorage.setItem(sessionKey, sessionId);
};

export const clearSessionFromStorage = () => {
  return sessionStorage.removeItem(sessionKey);
};

export type CommunityConnectorMode =
  | 'standalone'
  | 'maestro'
  | 'ICM'
  | 'maestroMedicare'
  | 'communityCare'
  | 'MedSup'
  | 'ICUE'
  | 'maestroIFP'
  | 'ACETEANDI'
  | 'ISETEANDI'
  | 'ACET'
  | '';

export const getModeFromStorage = (): CommunityConnectorMode => {
  return (sessionStorage.getItem(modeKey) as CommunityConnectorMode) ?? '';
};

export const saveModeToStorage = (mode: CommunityConnectorMode): void => {
  return sessionStorage.setItem(modeKey, mode);
};

export const clearModeFromStorage = () => {
  return sessionStorage.removeItem(modeKey);
};

export const getLoginRequestFromStorage = () => {
  return localStorage.getItem(loginRequestKey) ?? '';
};

export const saveLoginRequestToStorage = (loginRequest: string) => {
  return localStorage.setItem(loginRequestKey, loginRequest);
};

export const clearLoginRequestFromStorage = () => {
  return localStorage.removeItem(loginRequestKey);
};

export const getMemberDetailsFromStorage = (): SessionInfo | null => {
  try {
    const details = sessionStorage.getItem(memberDetailsKey);
    return details ? (JSON.parse(details) as SessionInfo) : null;
  } catch (error: any) {
    throw new Error(
      `Error getting member details, error was ${error}. Please make sure to select a member first.`
    );
  }
};

export const clearMemberDetailsFromStorage = () => {
  return sessionStorage.removeItem(memberDetailsKey);
};

export const clearAgentDetailsFromStorage = () => {
  return sessionStorage.removeItem(agentDetailsKey);
};

export const clearIdentifiedNeeds = (): void => {
  sessionStorage.removeItem(identified_NeedsKey);
};

export const clearVendor = (): void => {
  sessionStorage.removeItem(vendorKey);
};

export const saveVendor = (resp) => {
  return sessionStorage.setItem(vendorKey, resp);
};
